import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@r/components/data-table/data-table-column-header.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { OrderOrderCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { OrderOrderCollectionReadModelStatus } from '@r/api/generated/client.schemas.ts';
import type { DataTableFilterField } from '@r/types/index.ts';

type Order = OrderOrderCollectionReadModel;

export const useFilterFields = (options?: { admin?: boolean }): DataTableFilterField<Order>[] => {
  const { t } = useTranslation();
  const admin = options?.admin ?? false;

  const statusEnum = Object.values(OrderOrderCollectionReadModelStatus);
  const defaultFilters: DataTableFilterField<Order>[] = [
    {
      label: t('label.order.status'),
      value: 'status' as keyof Order,
      options: statusEnum.map((status) => ({
        label: t(status),
        value: status
      }))
    }
  ];

  if (!admin) {
    return defaultFilters;
  }

  return [
    {
      label: t('label.order.reference'),
      value: 'reference',
      placeholder: t('label.order.reference')
    },
    {
      label: t('label.order.created.at'),
      value: 'createdAt',
      placeholder: t('label.order.created.at')
    },
    {
      label: t('export.user.unique.code'),
      value: 'madeByUniqueCode',
      placeholder: t('export.user.unique.code')
    },
    {
      label: t('label.user.fullname'),
      value: 'madeByFullname',
      placeholder: t('label.user.fullname')
    },
    {
      label: t('label.user.email'),
      value: 'madeByEmail',
      placeholder: t('label.user.email')
    },
    {
      label: t('label.order.shop.name'),
      value: 'shopName',
      placeholder: t('label.order.shop.name')
    },
    {
      label: t('order.item'),
      value: 'productName',
      placeholder: t('order.item')
    },
    {
      label: t('order.product.id'),
      value: 'productId',
      placeholder: t('order.product.id')
    },
    {
      label: t('order.items.count'),
      value: 'productsCount',
      placeholder: t('order.items.count')
    },
    {
      label: t('label.order.point.amount'),
      value: 'pointAmount',
      placeholder: t('label.order.point.amount')
    },
    ...defaultFilters,
    {
      label: t('label.order.company.name'),
      value: 'companyName',
      placeholder: t('label.order.company.name')
    },
    {
      label: t('export.company.unique.code'),
      value: 'companyUniqueCode',
      placeholder: t('export.company.unique.code')
    }
  ];
};

export const useOrderColumns = (options?: { admin?: boolean }): ColumnDef<Order>[] => {
  const { t } = useTranslation();
  const admin = options?.admin ?? false;

  return [
    ...(admin
      ? [
          {
            accessorKey: 'id',
            header: t('id')
          }
        ]
      : []),
    {
      accessorKey: 'reference',
      header: t('label.order.reference')
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.order.created.at')} />
      ),
      cell: ({ row }) => {
        if (!row.original.createdAt) return '';

        return format(new Date(row.original.createdAt), "dd/MM/yyyy 'à' HH'h'mm", { locale: fr });
      }
    },
    {
      id: 'shopName',
      accessorFn: (row) => row.shops[0]?.displayName ?? '',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.order.shop.name')} />
      ),
      cell: ({ row }) => {
        return row.original.shops[0]?.displayName ?? '';
      }
    },
    {
      accessorKey: 'productsCount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('order.items.count')} />
      ),
      cell: ({ row }) => {
        return row.original.productsCount ?? 0;
      }
    },
    {
      accessorKey: 'pointAmount',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.order.point.amount')} />
      ),
      cell: ({ row }) => {
        const amount = Math.abs(row.original.point?.amount ?? 0);
        return pointsTranslation(t, amount, true);
      }
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('label.order.status')} />
      ),
      cell: ({ row }) => {
        return t(row.original.status);
      }
    },
    ...(admin
      ? [
          {
            accessorKey: 'company.name',
            header: t('label.order.company.name')
          }
        ]
      : [
          {
            accessorKey: 'madeBy.user.fullname',
            header: t('label.order.user')
          }
        ])
  ];
};
